/**
 * Python 代码生成器
 * 生成使用 requests 库的 Python HTTP 请求代码
 */

import { BaseCodeGenerator } from './base.js';

export class PythonGenerator extends BaseCodeGenerator {
  constructor() {
    super({
      language: 'python',
      library: 'requests',
      indent: '    ', // Python 标准缩进 4 个空格
      maxLineLength: 88 // Python 推荐行长度
    });
  }

  /**
   * 生成完整的 Python 代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 生成的 Python 代码
   */
  generate(parsedCurl) {
    this.validateParsedCurl(parsedCurl);

    const imports = this.generateImports(parsedCurl);
    const requestCode = this.generateRequestCode(parsedCurl);
    const executionCode = this.generateExecutionCode(parsedCurl);

    const parts = [];

    // 添加导入语句
    if (imports.length > 0) {
      parts.push(imports.join('\n'));
      parts.push(''); // 空行
    }

    // 添加请求代码
    parts.push(requestCode);

    // 添加执行代码
    if (executionCode) {
      parts.push('');
      parts.push(executionCode);
    }

    return parts.join('\n');
  }

  /**
   * 生成导入语句
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Array} 导入语句数组
   */
  generateImports(parsedCurl) {
    const imports = ['import requests'];

    // 检查是否需要额外的导入
    if (parsedCurl.data.type === 'json' && this.isComplexJson(parsedCurl.data.content)) {
      imports.push('import json');
    }

    if (parsedCurl.auth.type === 'digest') {
      imports.push('from requests.auth import HTTPDigestAuth');
    }

    if (parsedCurl.form && parsedCurl.form.length > 0) {
      // 对于文件上传，可能需要额外的处理
    }

    return imports;
  }

  /**
   * 生成请求代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 请求代码
   */
  generateRequestCode(parsedCurl) {
    const lines = [];

    // 添加注释
    lines.push(this.generateComment(`发送 ${parsedCurl.method} 请求到 ${parsedCurl.url}`));

    // URL
    lines.push(this.generateUrlCode(parsedCurl.url));

    // 请求头
    if (Object.keys(parsedCurl.headers).length > 0) {
      lines.push('');
      lines.push(this.generateHeadersCode(parsedCurl.headers));
    }

    // 请求数据
    if (parsedCurl.data.type !== 'none') {
      lines.push('');
      lines.push(this.generateDataCode(parsedCurl.data));
    }

    // 认证
    if (parsedCurl.auth.type !== 'none') {
      lines.push('');
      lines.push(this.generateAuthCode(parsedCurl.auth));
    }

    // Cookie
    if (Object.keys(parsedCurl.cookies).length > 0) {
      lines.push('');
      lines.push(this.generateCookiesCode(parsedCurl.cookies));
    }

    // 其他选项
    const optionsCode = this.generateOptionsCode(parsedCurl);
    if (optionsCode) {
      lines.push('');
      lines.push(optionsCode);
    }

    return lines.join('\n');
  }

  /**
   * 生成执行代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 执行代码
   */
  generateExecutionCode(parsedCurl) {
    const lines = [];

    // 构建请求调用参数
    const params = this.buildRequestParams(parsedCurl);

    // 生成请求调用
    let requestCall = 'response = requests.';
    requestCall += parsedCurl.method.toLowerCase();
    requestCall += '(';

    // 添加参数
    const paramStrings = [];
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        paramStrings.push(`${key}=${value}`);
      }
    }

    if (paramStrings.length > 0) {
      requestCall += '\n' + this.indentCode(paramStrings.join(',\n'), 1);
      requestCall += '\n)';
    } else {
      requestCall += ')';
    }

    lines.push(requestCall);

    // 添加响应处理
    lines.push('');
    lines.push('# 处理响应');
    lines.push('print(f"Status Code: {response.status_code}")');
    lines.push('print(f"Response: {response.text}")');

    // 如果是 JSON 响应，尝试解析
    if (this.shouldParseJsonResponse(parsedCurl)) {
      lines.push('');
      lines.push('try:');
      lines.push('    json_response = response.json()');
      lines.push('    print(f"JSON Response: {json_response}")');
      lines.push('except ValueError:');
      lines.push('    print("Response is not valid JSON")');
    }

    return lines.join('\n');
  }

  /**
   * 生成 URL 代码
   * @param {string} url - URL
   * @returns {string} URL 代码
   */
  generateUrlCode(url) {
    return `url = "${this.escapeString(url)}"`;
  }

  /**
   * 生成请求头代码
   * @param {Object} headers - 请求头对象
   * @returns {string} 请求头代码
   */
  generateHeadersCode(headers) {
    const lines = ['headers = {'];

    Object.entries(headers).forEach(([key, value], index, array) => {
      const comma = index < array.length - 1 ? ',' : '';
      lines.push(`    "${this.escapeString(key)}": "${this.escapeString(value)}"${comma}`);
    });

    lines.push('}');

    return lines.join('\n');
  }

  /**
   * 生成请求数据代码
   * @param {Object} data - 数据对象
   * @returns {string} 数据代码
   */
  generateDataCode(data) {
    switch (data.type) {
      case 'json':
        return this.generateJsonDataCode(data.content);
      case 'form':
        return this.generateFormDataCode(data.content);
      case 'raw':
        return this.generateRawDataCode(data.content);
      case 'binary':
        return this.generateBinaryDataCode(data.content);
      case 'urlencode':
        return this.generateUrlencodeDataCode(data.content);
      default:
        return '';
    }
  }

  /**
   * 生成 JSON 数据代码
   * @param {string} content - JSON 内容
   * @returns {string} JSON 数据代码
   */
  generateJsonDataCode(content) {
    if (this.isComplexJson(content)) {
      return `data = json.dumps(${content})`;
    } else {
      return `data = ${content}`;
    }
  }

  /**
   * 生成表单数据代码
   * @param {string} content - 表单内容
   * @returns {string} 表单数据代码
   */
  generateFormDataCode(content) {
    return `data = "${this.escapeString(content)}"`;
  }

  /**
   * 生成原始数据代码
   * @param {string} content - 原始内容
   * @returns {string} 原始数据代码
   */
  generateRawDataCode(content) {
    return `data = "${this.escapeString(content)}"`;
  }

  /**
   * 生成二进制数据代码
   * @param {string} content - 二进制内容
   * @returns {string} 二进制数据代码
   */
  generateBinaryDataCode(content) {
    return `data = ${content}`;
  }

  /**
   * 生成 URL 编码数据代码
   * @param {string} content - URL 编码内容
   * @returns {string} URL 编码数据代码
   */
  generateUrlencodeDataCode(content) {
    return `data = "${this.escapeString(content)}"`;
  }

  /**
   * 生成认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 认证代码
   */
  generateAuthCode(auth) {
    switch (auth.type) {
      case 'basic':
        return this.generateBasicAuthCode(auth);
      case 'bearer':
        return this.generateBearerAuthCode(auth);
      case 'digest':
        return this.generateDigestAuthCode(auth);
      default:
        return '';
    }
  }

  /**
   * 生成基本认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 基本认证代码
   */
  generateBasicAuthCode(auth) {
    if (auth.username && auth.password !== undefined) {
      return `auth = ("${this.escapeString(auth.username)}", "${this.escapeString(auth.password || '')}")`;
    }
    return '';
  }

  /**
   * 生成 Bearer 认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} Bearer 认证代码
   */
  generateBearerAuthCode(auth) {
    return `headers["Authorization"] = "Bearer ${this.escapeString(auth.token)}"`;
  }

  /**
   * 生成摘要认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 摘要认证代码
   */
  generateDigestAuthCode(auth) {
    return `auth = HTTPDigestAuth("${this.escapeString(auth.username)}", "${this.escapeString(auth.password || '')}")`;
  }

  /**
   * 生成 Cookie 代码
   * @param {Object} cookies - Cookie 对象
   * @returns {string} Cookie 代码
   */
  generateCookiesCode(cookies) {
    const lines = ['cookies = {'];

    Object.entries(cookies).forEach(([key, value], index, array) => {
      const comma = index < array.length - 1 ? ',' : '';
      lines.push(`    "${this.escapeString(key)}": "${this.escapeString(value)}"${comma}`);
    });

    lines.push('}');

    return lines.join('\n');
  }

  /**
   * 生成选项代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 选项代码
   */
  generateOptionsCode(parsedCurl) {
    const options = [];

    if (parsedCurl.insecure) {
      options.push('verify = False');
    }

    if (parsedCurl.followRedirects) {
      options.push('allow_redirects = True');
    }

    if (parsedCurl.connectTimeout > 0) {
      options.push(`timeout = ${parsedCurl.connectTimeout}`);
    }

    if (parsedCurl.maxTime > 0) {
      options.push(`timeout = ${parsedCurl.maxTime}`);
    }

    if (parsedCurl.proxy) {
      options.push(this.generateProxyCode(parsedCurl.proxy));
    }

    return options.join('\n');
  }

  /**
   * 生成代理代码
   * @param {string} proxy - 代理 URL
   * @returns {string} 代理代码
   */
  generateProxyCode(proxy) {
    return `proxies = {\n    "http": "${this.escapeString(proxy)}",\n    "https": "${this.escapeString(proxy)}"\n}`;
  }

  /**
   * 构建请求参数
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Object} 请求参数
   */
  buildRequestParams(parsedCurl) {
    const params = {
      url: 'url'
    };

    if (Object.keys(parsedCurl.headers).length > 0) {
      params.headers = 'headers';
    }

    if (parsedCurl.data.type !== 'none') {
      params.data = 'data';
    }

    if (parsedCurl.auth.type !== 'none' && parsedCurl.auth.type !== 'bearer') {
      params.auth = 'auth';
    }

    if (Object.keys(parsedCurl.cookies).length > 0) {
      params.cookies = 'cookies';
    }

    // 添加选项参数
    if (parsedCurl.insecure) {
      params.verify = 'verify';
    }

    if (parsedCurl.followRedirects) {
      params.allow_redirects = 'allow_redirects';
    }

    if (parsedCurl.connectTimeout > 0 || parsedCurl.maxTime > 0) {
      params.timeout = 'timeout';
    }

    if (parsedCurl.proxy) {
      params.proxies = 'proxies';
    }

    return params;
  }

  /**
   * 检查是否为复杂的 JSON
   * @param {string} content - JSON 内容
   * @returns {boolean} 是否为复杂 JSON
   */
  isComplexJson(content) {
    try {
      const parsed = JSON.parse(content);
      // 如果是对象或数组，认为是复杂的
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  }

  /**
   * 检查是否应该解析 JSON 响应
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {boolean} 是否应该解析 JSON
   */
  shouldParseJsonResponse(parsedCurl) {
    // 检查 Accept 头或 Content-Type 头
    const acceptHeader = parsedCurl.headers['Accept'] || parsedCurl.headers['accept'];
    const contentTypeHeader = parsedCurl.headers['Content-Type'] || parsedCurl.headers['content-type'];

    return (acceptHeader && acceptHeader.includes('application/json')) ||
           (contentTypeHeader && contentTypeHeader.includes('application/json'));
  }

  /**
   * 生成注释
   * @param {string} text - 注释文本
   * @returns {string} 注释代码
   */
  generateComment(text) {
    return `# ${text}`;
  }

  /**
   * 转义字符串（Python 风格）
   * @param {string} str - 要转义的字符串
   * @returns {string} 转义后的字符串
   */
  escapeString(str) {
    return str
      .replace(/\\/g, '\\\\')  // 反斜杠
      .replace(/"/g, '\\"')    // 双引号
      .replace(/\n/g, '\\n')   // 换行符
      .replace(/\r/g, '\\r')   // 回车符
      .replace(/\t/g, '\\t');  // 制表符
  }
}